import type { ConversationCompletion, ProviderContinuationRecovery, ToolActivityOutcome } from "~/shared/agent-core/conversation-contract";
import type { ActionBinding, ActionDisplayedSelection, ActionDraftContext, ActionIntent } from "~/shared/ai/assistant-action-artifact";
import type { AssistantAttachmentEnvelope } from "~/shared/ai/assistant-attachment-contract";
import type { AssistantEntrySurface } from "~/shared/ai/assistant-entry-surface";
import type { AgentEvent, DebugPayload } from "~/shared/ai/assistant-event-contract";
import type { ActiveReportDescriptor } from "~/utils/types/assistant-report-artifact";
import { ADDED_REPORT_FIELD_KINDS, fieldsForKinds } from "~/utils/types/assistant-report-artifact";

import { parseAgentEvent } from "./agent-event-parser";

export type StreamCompletion = ConversationCompletion;

export interface StreamCallbacks {
  onConversationReady?: (conversationId: string) => void;
  onContent: (text: string) => void;
  onComplete: (completion: StreamCompletion) => void;
  onError: (message: string) => void;
  onDebug?: (payload: DebugPayload) => void;
  onToolCall?: (name: string, args: Record<string, unknown>, callId?: string) => void;
  onToolResult?: (name: string, outcome: ToolActivityOutcome, durationMs: number, callId?: string) => void;
  onStatus?: (message: string) => void;
  onPhase?: (event: Extract<AgentEvent, { type: "phase" }>) => void;
  onActionProgress?: (event: Extract<AgentEvent, { type: "action_progress" }>) => void;
  onActionItemOutcome?: (event: Extract<AgentEvent, { type: "action_item_outcome" }>) => void;
  onActionLifecycle?: (event: Extract<AgentEvent, { type: "action_lifecycle" }>) => void;
}

export interface StreamAssistantMessageArgs {
  message: string;
  newThread: boolean;
  turnId: string;
  surface: AssistantEntrySurface;
  debugMode: boolean;
  activeReportDescriptor?: ActiveReportDescriptor;
  actionBindings?: ActionBinding[];
  displayedSelections?: ActionDisplayedSelection[];
  actionIntent?: ActionIntent;
  actionDraftContext?: ActionDraftContext;
  attachmentEnvelope?: AssistantAttachmentEnvelope;
  callbacks: StreamCallbacks;
  signal: AbortSignal;
}

export interface StreamAssistantRecoveryArgs {
  providerRecovery: ProviderContinuationRecovery;
  turnId: string;
  surface: AssistantEntrySurface;
  callbacks: StreamCallbacks;
  signal: AbortSignal;
}

const INVALID_ACTIVE_REPORT_DESCRIPTOR = "Invalid active report descriptor";

const readErrorMessage = async (response: Response): Promise<string> => {
  const errorData: unknown = await response.json().catch(() => ({ error: "Request failed" }));
  return typeof errorData === "object" && errorData !== null && "error" in errorData && typeof errorData.error === "string"
    ? errorData.error
    : `HTTP ${response.status}`;
};

/**
 * A server that predates an added report kind refuses a descriptor holding it. The follow-up is
 * retried once with those kinds sent as text, the same mapping the server applies for old tabs.
 */
const descriptorForOlderServer = (descriptor: ActiveReportDescriptor | undefined): ActiveReportDescriptor | undefined => {
  if (!descriptor) {
    return undefined;
  }
  const availableFields = fieldsForKinds(descriptor.availableFields, undefined);
  return availableFields === descriptor.availableFields ? undefined : { ...descriptor, availableFields };
};

/** Framework-free SSE client. Its caller owns lifetime, state, and cancellation. */
const streamAssistantRequest = async (args: StreamAssistantMessageArgs | StreamAssistantRecoveryArgs): Promise<boolean> => {
  const { callbacks } = args;
  let errorReceived = false;
  try {
    const post = (activeReportDescriptor: ActiveReportDescriptor | undefined) =>
      fetch("/api/rest/ai-chat-stream", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-shelfcycle-report-kinds": ADDED_REPORT_FIELD_KINDS.join(",") },
        body: JSON.stringify(
          "providerRecovery" in args
            ? {
                providerRecovery: args.providerRecovery,
                agentType: "assistant",
                turnId: args.turnId,
                surface: args.surface,
              }
            : {
                message: args.message,
                newThread: args.newThread,
                debugMode: args.debugMode,
                agentType: "assistant",
                turnId: args.turnId,
                surface: args.surface,
                activeReportDescriptor,
                actionBindings: args.actionBindings,
                displayedSelections: args.displayedSelections,
                actionIntent: args.actionIntent,
                actionDraftContext: args.actionDraftContext,
                attachmentEnvelope: args.attachmentEnvelope,
              },
        ),
        signal: args.signal,
      });
    const activeReportDescriptor = "providerRecovery" in args ? undefined : args.activeReportDescriptor;
    let response = await post(activeReportDescriptor);
    if (!response.ok) {
      const message = await readErrorMessage(response);
      const olderServerDescriptor =
        response.status === 400 && message === INVALID_ACTIVE_REPORT_DESCRIPTOR
          ? descriptorForOlderServer(activeReportDescriptor)
          : undefined;
      if (!olderServerDescriptor) {
        throw new Error(message);
      }
      response = await post(olderServerDescriptor);
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }
    }
    if (!response.body) {
      throw new Error("No response body");
    }
    const conversationId = response.headers.get("X-Assistant-Conversation-Id");
    if (conversationId) {
      callbacks.onConversationReady?.(conversationId);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    const cancelReader = async () => {
      try {
        await reader.cancel();
      } catch {
        // Terminal classification is authoritative; reader cleanup is best-effort.
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      let boundaryIndex: number;
      while ((boundaryIndex = buffer.indexOf("\n\n")) !== -1) {
        const eventChunk = buffer.slice(0, boundaryIndex);
        buffer = buffer.slice(boundaryIndex + 2);
        const data = eventChunk
          .split("\n")
          .filter(line => line.startsWith("data: "))
          .map(line => line.slice(6))
          .join("\n")
          .trim();
        if (!data || data === "[DONE]") {
          continue;
        }
        try {
          const event = parseAgentEvent(JSON.parse(data));
          if (!event) {
            continue;
          }
          switch (event.type) {
            case "status":
              if (!errorReceived) {
                callbacks.onStatus?.(event.message);
              }
              break;
            case "phase":
              if (!errorReceived) {
                callbacks.onPhase?.(event);
              }
              break;
            case "content":
              if (!errorReceived) {
                callbacks.onContent(event.text);
              }
              break;
            case "debug":
              if (!errorReceived) {
                callbacks.onDebug?.(event.payload);
              }
              break;
            case "tool_call":
              if (!errorReceived) {
                callbacks.onToolCall?.(event.name, event.args, event.callId);
              }
              break;
            case "tool_result":
              if (!errorReceived) {
                callbacks.onToolResult?.(event.name, event.outcome, event.durationMs, event.callId);
              }
              break;
            case "action_progress":
              callbacks.onActionProgress?.(event);
              break;
            case "action_item_outcome":
              callbacks.onActionItemOutcome?.(event);
              break;
            case "action_lifecycle":
              callbacks.onActionLifecycle?.(event);
              break;
            case "error":
              errorReceived = true;
              callbacks.onError(event.message);
              await cancelReader();
              return false;
            case "done":
              if (!errorReceived) {
                if (event.turnId !== args.turnId) {
                  callbacks.onError("The response could not be matched to this request.");
                  await cancelReader();
                  return false;
                }
                await cancelReader();
                callbacks.onComplete({
                  conversationId: event.threadId,
                  turnId: event.turnId,
                  completionDisposition: event.completionDisposition,
                  reportArtifactCommit: event.reportArtifactCommit,
                  actionProposalCommit: event.actionProposalCommit,
                });
                return true;
              }
              break;
          }
        } catch {
          // Malformed SSE frames are ignored without changing the established stream grammar.
        }
      }
    }
    if (!errorReceived) {
      callbacks.onError("The response ended before it completed.");
    }
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      return false;
    }
    callbacks.onError(error instanceof Error ? error.message : "An unexpected error occurred");
  }
  return false;
};

export const streamAssistantMessage = (args: StreamAssistantMessageArgs): Promise<boolean> => streamAssistantRequest(args);

export const streamAssistantRecovery = (args: StreamAssistantRecoveryArgs): Promise<boolean> => streamAssistantRequest(args);
